import { useQuery } from "@tanstack/react-query";
import { Alert, Card, Col, Row, Spin, Table, Tag } from "antd";
import { FaArrowDown, FaArrowUp, FaClock, FaWallet } from "react-icons/fa";

import {
  getPayoutsBalance,
  getPayoutsHistory,
} from "../../actions/acc_actions";
import ContentLayout from "../../components/layout/ContentLayout";

export default function Wallet({ accUrn }) {
  const {
    isLoading: balancesLoading,
    data: balancesData,
    isError,
  } = useQuery({
    queryKey: ["balances", accUrn],
    queryFn: () => getPayoutsBalance(accUrn),
    enabled: !!accUrn,
  });

  const { isLoading: transactionsLoading, data: transactionsData } = useQuery({
    queryKey: ["transactions", accUrn],
    queryFn: () => getPayoutsHistory(accUrn),
    enabled: !!accUrn,
  });

  if (balancesLoading || transactionsLoading) {
    return (
      <div className="wallet-loading">
        <Spin size="large" />
      </div>
    );
  }

  if (isError) {
    return (
      <Alert
        type="error"
        message="Failed to load wallet data"
        description="Please try again later."
      />
    );
  }

  const clearedBalance = balancesData?.balance?.clearedBalance ?? 0;
  const pendingBalance = balancesData?.balance?.pendingBalance ?? 0;

  const transactions = (transactionsData || [])?.map((item) => ({
    id: item.id,
    productName: item.order_item?.product?.name_en || "-",
    type: item.transaction_type,
    amount: Number(item.amount),
    commission: Number(item.commission_amount),
    status: item.status,
    date: new Date(item.created_at).toLocaleDateString(),
  }));

  const columns = [
    {
      title: "Product",
      dataIndex: "productName",
      responsive: ["md"],
    },
    {
      title: "Type",
      dataIndex: "type",
      render: (v) =>
        v === "sale" ? (
          <Tag color="green">
            <FaArrowUp /> Sale
          </Tag>
        ) : (
          <Tag color="red">
            <FaArrowDown /> Refund
          </Tag>
        ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      render: (v) => `$${v.toFixed(2)}`,
    },
    {
      title: "Commission",
      dataIndex: "commission",
      responsive: ["lg"],
      render: (v) => `$${v.toFixed(2)}`,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (v) => {
        const color =
          v === "pending_clearance"
            ? "orange"
            : v === "completed"
              ? "green"
              : "red";
        return <Tag color={color}>{v.replace("_", " ")}</Tag>;
      },
    },
    {
      title: "Date",
      dataIndex: "date",
      responsive: ["md"],
    },
  ];

  return (
    <ContentLayout
      header={
        <>
          <h1>
            Wallet <br /> <span>Your Financial Overview</span>
          </h1>

          {/* ✅ Enhanced Cards */}
          <Row gutter={[16, 16]} className="wallet-cards">
            <Col xs={24} sm={12} lg={8}>
              <Card className="wallet-card gradient-blue">
                <div className="card-content">
                  <FaWallet className="card-icon" />
                  <div>
                    <h3>Total Balance</h3>
                    <p>${(clearedBalance + pendingBalance).toFixed(2)}</p>
                  </div>
                </div>
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={8}>
              <Card className="wallet-card gradient-green">
                <div className="card-content">
                  <FaArrowUp className="card-icon" />
                  <div>
                    <h3>Cleared Balance</h3>
                    <p>${clearedBalance.toFixed(2)}</p>
                  </div>
                </div>
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={8}>
              <Card className="wallet-card gradient-orange">
                <div className="card-content">
                  <FaClock className="card-icon" />
                  <div>
                    <h3>Pending Balance</h3>
                    <p>${pendingBalance.toFixed(2)}</p>
                  </div>
                </div>
              </Card>
            </Col>
          </Row>
        </>
      }
    >
      {/* ✅ Responsive Table */}
      <Card className="wallet-table-card">
        <h3>Transactions</h3>
        <Table
          columns={columns}
          dataSource={transactions}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          scroll={{ x: 600 }} // ✅ makes table responsive
        />
      </Card>
    </ContentLayout>
  );
}
